/**
 * Bulk Order Widget — bulk-order-widget.js
 * Place in: assets/bulk-order-widget.js
 * Handles: colour switching, size qty controls,
 *           tier pricing, totals, cart submission
 */

(function () {
  'use strict';

  const data = window.bowProductData;
  if (!data) return;

  // ── State ──────────────────────────────────────────────
  let selectedColour = null;
  let quantities = {}; // { size: qty }

  // ── DOM refs ───────────────────────────────────────────
  const widget       = document.getElementById('bulk-order-widget');
  const priceDisplay = document.getElementById('bow-price-display');
  const tierRows     = document.querySelectorAll('.bow-tier-row');
  const sizeGrid     = document.getElementById('bow-size-grid');
  const savingsNote  = document.getElementById('bow-savings-note');
  const totalEl      = document.getElementById('bow-total');
  const atcBtn       = document.getElementById('bow-atc-btn');
  const hiddenInputs = document.getElementById('bow-hidden-inputs');
  const colourName   = document.getElementById('bow-colour-name');

  if (!widget) return;

  // ── Helpers ────────────────────────────────────────────
  function formatMoney(pence) {
    return '£' + (pence / 100).toFixed(2);
  }

  function getTier(totalQty) {
    if (totalQty < 1) return data.tiers[0];
    return data.tiers.find(t => totalQty >= t.min && totalQty <= t.max) || data.tiers[0];
  }

  function getTierPrice(tier) {
    return Math.round(data.basePrice * (100 - tier.save) / 100);
  }

  function totalQuantity() {
    return Object.values(quantities).reduce((a, b) => a + b, 0);
  }

  function getVariant(colour, size) {
    return data.variants.find(v => {
      const colourMatch = data.colourOptionIndex === -1 || v.colour === colour;
      const sizeMatch   = data.sizeOptionIndex   === -1 || v.size   === size;
      return colourMatch && sizeMatch;
    });
  }

  // ── Colour switching ───────────────────────────────────
  function getUrlVariantId() {
    const raw = new URLSearchParams(window.location.search).get('variant');
    return raw ? parseInt(raw, 10) : null;
  }

  function initColour() {
    // Prefer the variant actually shown on the page (from the ?variant= URL param
    // Dawn sets when a variant is selected) so the widget's colour matches the
    // product photo the customer sees. Previously this always defaulted to the
    // first swatch in the DOM, which could silently differ from what was on
    // screen (e.g. a Light Grey variant added to basket while a Navy jacket was
    // displayed) — customers had no way to notice before checking their basket.
    const urlVariantId = getUrlVariantId();
    const urlVariant = urlVariantId ? data.variants.find(v => v.id === urlVariantId) : null;

    if (urlVariant && urlVariant.colour) {
      selectedColour = urlVariant.colour;
    } else {
      const firstSwatch = widget.querySelector('.bow-swatch');
      if (firstSwatch) selectedColour = firstSwatch.dataset.colour;
    }

    // Keep the swatch UI and colour label in sync with whichever colour we picked.
    if (selectedColour) {
      const activeSwatch = [...widget.querySelectorAll('.bow-swatch')].find(
        (s) => s.dataset.colour === selectedColour
      );
      if (activeSwatch) {
        widget.querySelectorAll('.bow-swatch').forEach((s) => s.classList.remove('bow-swatch-active'));
        activeSwatch.classList.add('bow-swatch-active');
      }
      if (colourName) colourName.textContent = selectedColour;
    }
  }

function handleColourClick(e) {
    const swatch = e.currentTarget;
    const colour = swatch.dataset.colour;
    if (colour === selectedColour) return;

    selectedColour = colour;

    widget.querySelectorAll('.bow-swatch').forEach(s => s.classList.remove('bow-swatch-active'));
    swatch.classList.add('bow-swatch-active');

    if (colourName) colourName.textContent = colour;

    // Reset all quantities when colour changes
    quantities = {};

    // ── Trigger Dawn's native image switch ──────────────
    // Find the first available variant for this colour
    const matchingVariant = data.variants.find(v => v.colour === colour && v.available);
    if (matchingVariant) {
      // Update Dawn's native variant selector so it switches the image
      const variantSelects = document.querySelectorAll('variant-selects, variant-radios');
      variantSelects.forEach(el => {
        const options = el.querySelectorAll('select, input[type="radio"]');
        options.forEach(option => {
          if (option.tagName === 'SELECT') {
            const colourOption = [...option.options].find(o => o.text === colour);
            if (colourOption) {
              option.value = colourOption.value;
              option.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else if (option.type === 'radio' && option.value === colour) {
            option.checked = true;
            option.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });

      // Fallback — dispatch Shopify's variant change event directly
      document.dispatchEvent(new CustomEvent('variant:changed', {
        bubbles: true,
        detail: { variant: matchingVariant }
      }));
    }
    // ────────────────────────────────────────────────────

    // Re-render size grid with new stock levels
    renderSizeGrid();
    updateTotals();
  }

  // ── Size grid rendering ────────────────────────────────
  function renderSizeGrid() {
    const cards = sizeGrid.querySelectorAll('.bow-size-card');

    cards.forEach(card => {
      const size = card.dataset.size;
      const variant = getVariant(selectedColour, size);
      const stock = variant ? Math.max(0, variant.stock) : 0;
      const qty = quantities[size] || 0;

      // Update card state
      card.dataset.stock = stock;
      card.dataset.variantId = variant ? variant.id : '';

      // Stock labels
      const stockLabel = card.querySelector('.bow-stock-label');
      const stockAvail = card.querySelector('.bow-stock-avail');

      if (stock <= 0) {
        card.classList.add('bow-out-of-stock');
        if (stockLabel) { stockLabel.textContent = 'Out Of Stock'; stockLabel.className = 'bow-stock-label bow-stock-out'; }
        if (stockAvail) stockAvail.textContent = '';
        quantities[size] = 0;
      } else {
        card.classList.remove('bow-out-of-stock');
        if (stock <= 20) {
          if (stockLabel) { stockLabel.textContent = 'Low Stock'; stockLabel.className = 'bow-stock-label bow-stock-low'; }
        } else {
          if (stockLabel) { stockLabel.textContent = 'In Stock'; stockLabel.className = 'bow-stock-label bow-stock-in'; }
        }
        if (stockAvail) stockAvail.textContent = stock + ' Available';
      }

      // Qty display
      const valEl = card.querySelector('.bow-qty-val');
      if (valEl) valEl.textContent = qty;
    });
  }

  // ── Qty buttons ────────────────────────────────────────
  function handleQtyClick(e) {
    const btn = e.currentTarget;
    const card = btn.closest('.bow-size-card');
    if (!card || card.classList.contains('bow-out-of-stock')) return;

    const size  = card.dataset.size;
    const stock = parseInt(card.dataset.stock, 10) || 0;
    const isPlus = btn.classList.contains('bow-qty-plus');

    if (!quantities[size]) quantities[size] = 0;

    if (isPlus) {
      if (quantities[size] < stock) quantities[size]++;
    } else {
      if (quantities[size] > 0) quantities[size]--;
    }

    const valEl = card.querySelector('.bow-qty-val');
    if (valEl) valEl.textContent = quantities[size];

    updateTotals();
  }

  // ── Totals + tier highlight ────────────────────────────
  function updateTotals() {
    const total = totalQuantity();
    const tier  = getTier(total);
    const tierPrice = getTierPrice(tier);
    const totalPence = total * tierPrice;

    // Update price hero
    if (priceDisplay) priceDisplay.textContent = formatMoney(tierPrice);

    // Highlight active tier row
    tierRows.forEach(row => {
      const rowMin = parseInt(row.dataset.min, 10);
      const rowMax = parseInt(row.dataset.max, 10);
      if (total >= rowMin && total <= rowMax) {
        row.classList.add('bow-tier-active');
      } else {
        row.classList.remove('bow-tier-active');
      }
    });

    // Savings callout
    if (tier.save > 0 && total > 0) {
      const savedPence = (data.basePrice - tierPrice) * total;
      savingsNote.style.display = 'block';
      savingsNote.textContent = '✓ You\'re Saving ' + formatMoney(savedPence) + ' With This Quantity (' + tier.save + '% Off)';
    } else {
      savingsNote.style.display = 'none';
    }

    // Total display
    if (totalEl) totalEl.textContent = formatMoney(totalPence);

    // ATC button
    if (total === 0) {
      atcBtn.textContent = 'Add 0 Items To Basket';
      atcBtn.classList.add('bow-atc-empty');
      atcBtn.classList.remove('bow-atc-ready');
      atcBtn.disabled = true;
    } else {
      atcBtn.textContent = 'Add ' + total + ' Item' + (total === 1 ? '' : 's') + ' To Basket — ' + formatMoney(totalPence);
      atcBtn.classList.remove('bow-atc-empty');
      atcBtn.classList.add('bow-atc-ready');
      atcBtn.disabled = false;
    }

    // Build hidden inputs for cart form
    buildCartInputs();
  }

  // ── Cart form ──────────────────────────────────────────
  function buildCartInputs() {
    if (!hiddenInputs) return;
    hiddenInputs.innerHTML = '';

    Object.entries(quantities).forEach(([size, qty]) => {
      if (qty < 1) return;
      const variant = getVariant(selectedColour, size);
      if (!variant) return;

      // id[variantId] = qty  (Shopify multi-line cart add format)
      const idInput  = document.createElement('input');
      idInput.type   = 'hidden';
      idInput.name   = 'items[][id]';
      idInput.value  = variant.id;
      hiddenInputs.appendChild(idInput);

      const qtyInput  = document.createElement('input');
      qtyInput.type   = 'hidden';
      qtyInput.name   = 'items[][quantity]';
      qtyInput.value  = qty;
      hiddenInputs.appendChild(qtyInput);
    });
  }

  // ── Cart form submit ───────────────────────────────────
  function handleCartSubmit(e) {
    e.preventDefault();

    const total = totalQuantity();
    if (total === 0) return;

    const form = document.getElementById('bow-cart-form');
    const items = [];

    Object.entries(quantities).forEach(([size, qty]) => {
      if (qty < 1) return;
      const variant = getVariant(selectedColour, size);
      if (!variant) return;
      items.push({ id: variant.id, quantity: qty });
    });

    if (items.length === 0) return;

    // Disable button during request
    atcBtn.disabled = true;
    atcBtn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    })
    .then(res => res.json())
.then((response) => {
      atcBtn.textContent = '✓ Added To Basket!';
      atcBtn.classList.remove('bow-atc-ready');
      atcBtn.classList.add('bow-atc-success');

      // ── Trigger Dawn's cart drawer to open ──────────────
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          document.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart }
          }));
        });

      // ── Rosewear branding wizard ─────────────────────────
      // This widget has its own add-to-cart flow (it never goes through
      // Dawn's standard product-form.js), so this is the real, correct
      // place to open the branding wizard — right after a confirmed
      // successful add, using the item(s) /cart/add.js just returned.
      if (window.RosewearBranding && response && response.items && response.items.length) {
        // A single bulk submission can add several distinct cart lines at once
        // (e.g. 5x Small + 5x Medium are two separate lines). Queue every line
        // that was added — not just the first — so each size gets its own
        // branding opportunity instead of only the first line in the response.
        response.items.forEach((addedItem) => {
          window.RosewearBranding.open({
            cartItemKey: addedItem.key,
            productTitle: addedItem.product_title,
            variantTitle: addedItem.variant_title,
            quantity: addedItem.quantity,
            imageUrl: addedItem.image,
            mode: 'included',
          });
        });
      }

      // ── Toast notification ───────────────────────────────
      const existingToast = document.getElementById('bow-toast');
      if (existingToast) existingToast.remove();

      const toast = document.createElement('div');
      toast.id = 'bow-toast';
      toast.innerHTML = `
        <div style="
          position: fixed;
          bottom: 24px;
          right: 24px;
          background: #1a2744;
          color: #fff;
          padding: 14px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          z-index: 99999;
          box-shadow: 0 4px 16px rgba(0,0,0,0.2);
          display: flex;
          align-items: center;
          gap: 10px;
          animation: bowSlideIn 0.3s ease;
        ">
          <span style="font-size:18px;">✓</span>
          <span>Items Added To Your Basket</span>
        </div>
        <style>
          @keyframes bowSlideIn {
            from { transform: translateY(20px); opacity: 0; }
            to   { transform: translateY(0);    opacity: 1; }
          }
        </style>
      `;
      document.body.appendChild(toast);

      setTimeout(() => {
        quantities = {};
        renderSizeGrid();
        updateTotals();
        atcBtn.classList.remove('bow-atc-success');
        const t = document.getElementById('bow-toast');
        if (t) t.remove();
      }, 3000);
    })
    .catch(() => {
      atcBtn.disabled = false;
      atcBtn.textContent = 'Something Went Wrong — Please Try Again';
      atcBtn.classList.add('bow-atc-error');
      setTimeout(() => {
        atcBtn.classList.remove('bow-atc-error');
        updateTotals();
      }, 3000);
    });
  }

  // ── Tier row click — jump to that tier's minimum ───────
  tierRows.forEach(row => {
    row.addEventListener('click', () => {
      const min = parseInt(row.dataset.min, 10);
      const cur = totalQuantity();
      if (cur >= min) return;

      // Distribute minimum quantity across available sizes
      let needed = min - cur;
      const cards = [...sizeGrid.querySelectorAll('.bow-size-card:not(.bow-out-of-stock)')];

      cards.forEach(card => {
        if (needed <= 0) return;
        const size  = card.dataset.size;
        const stock = parseInt(card.dataset.stock, 10) || 0;
        const cur   = quantities[size] || 0;
        const add   = Math.min(needed, stock - cur);
        if (add > 0) {
          quantities[size] = cur + add;
          needed -= add;
        }
      });

      renderSizeGrid();
      updateTotals();
    });
  });

  // ── Bind events ────────────────────────────────────────
  widget.querySelectorAll('.bow-swatch').forEach(s => {
    s.addEventListener('click', handleColourClick);
  });

  sizeGrid.addEventListener('click', e => {
    const btn = e.target.closest('.bow-qty-btn');
    if (btn) handleQtyClick({ currentTarget: btn });
  });

  document.getElementById('bow-cart-form').addEventListener('submit', handleCartSubmit);

  // ── Init ───────────────────────────────────────────────
  initColour();
  renderSizeGrid();
  updateTotals();

})();
